import { Form, Formik, useFormikContext } from 'formik'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import * as Yup from 'yup'
import { isCurrency } from '../../../lib/Validations'
import Headline from '../../Elements/Headline'
import { Modal } from '../../Elements/Modal'
import Button from '../../Formik/Button'
import Checkbox from '../../Formik/Checkbox'
import Input from '../../Formik/Input'
import Select from '../../Formik/Select'
import { AccountingType } from '../lib/Enums/AccountingType'
import { useCreateAccounting } from '../lib/Hooks/Accounting'

type Props = {
  showModal: boolean
  toggleModal: (show: boolean) => void
}

const CreateAccounting: FC<Props> = ({ showModal, toggleModal }) => {
  const { t } = useTranslation(['accounting', 'common'])
  const { mutateAsync: createAccounting } = useCreateAccounting()
  const navigate = useNavigate()

  return (
    <>
      <Modal show={showModal} onHide={() => toggleModal(false)}>
        <Formik
          initialValues={{
            type: AccountingType.REGULAR,
            budget: '',
            dedicated_account: true,
            starting_balance: 0,
          }}
          validationSchema={Yup.object({
            type: Yup.string().required(t('common:validation.required')),
            budget: Yup.number(),
            dedicated_account: Yup.boolean().required(
              t('common:validation.required'),
            ),
            starting_balance: Yup.string()
              .transform((currentValue) => {
                // First remove all danish thousand seperators (.) and then replace the danish delimiter seperator (,) with the english one (.).
                // This makes it's possible to save floats.
                return currentValue.replaceAll('.', '').replaceAll(',', '.')
              })
              .required(t('common:validation.required'))
              .test(isCurrency),
          })}
          onSubmit={async (values) => {
            const accounting = await createAccounting({
              ...values,
              budget: parseInt(values.budget, 10),
            })

            return navigate(`/accounting/${accounting.id}/`)
          }}
        >
          <CreateAccountingForm />
        </Formik>
      </Modal>
    </>
  )
}

const CreateAccountingForm = () => {
  const { t } = useTranslation(['accounting'])
  const {
    values: { type, dedicated_account },
  } = useFormikContext<any>()

  return (
    <Form>
      <Headline className='mb-4'>{t('accounting:createAccounting.title')}</Headline>
      <p className='mb-2 font-bold'>
        {t('accounting:createAccounting.selectType')}
      </p>
      <Select name='type' className='mb-3'>
        <option value={AccountingType.REGULAR}>
          {t('accounting:createAccounting.options.accounting')}
        </option>
        <option value={AccountingType.YEAR}>
          {t('accounting:createAccounting.options.year')}
        </option>
        <option value={AccountingType.MONTH}>
          {t('accounting:createAccounting.options.month')}
        </option>
        <option value={AccountingType.WEEK}>
          {t('accounting:createAccounting.options.week')}
        </option>
      </Select>
      <p className='mb-5 italic'>
        {type === AccountingType.REGULAR && (
          <span>{t('accounting:createAccounting.descriptions.regular')}</span>
        )}
        {type === AccountingType.MONTH && (
          <span>{t('accounting:createAccounting.descriptions.month')}</span>
        )}
        {type === AccountingType.WEEK && (
          <span>{t('accounting:createAccounting.descriptions.week')}</span>
        )}
      </p>
      <p className='mb-2 font-bold'>{t('accounting:createAccounting.budget')}</p>
      <Input
        name='budget'
        placeholder={t('accounting:createAccounting.budgetPlaceholder')}
        type='number'
        className='mb-2'
      ></Input>
      <p className='mb-7 italic'>
        {t('accounting:createAccounting.budgetHelp')}
      </p>
      <Checkbox name='dedicated_account'>
        {t('accounting:createAccounting.dedicatedAccount')}
      </Checkbox>
      {dedicated_account && (
        <>
          <p className='mt-7 mb-2 font-bold'>
            {t('accounting:createAccounting.balance')}
          </p>
          <Input
            type='number'
            name='starting_balance'
            placeholder={t('accounting:createAccounting.balancePlaceholder')}
          />
          <p className='italic mt-2'>
            {t('accounting:createAccounting.balanceHelp')}
          </p>
        </>
      )}
      <Button type='submit' className='mt-5'>
        {t('accounting:createAccounting.submit')}
      </Button>
    </Form>
  )
}

export default CreateAccounting
